import { type RequestContext } from '@cx-web/domain-types';
import { CATEGORY_ORDERING_CONFIGS, REPUB_CONFIG } from '@cx-web/repub-config';
import { getRepubMetaFromRepub } from '@cx-web/utils';

import { DateRefinement } from '~/components/BettingInsights/components/Filters';
import { MatchupCard } from '~/components/BettingInsights/components/MatchupCard';
import { Autocomplete } from '~/components/BettingInsights/components/Search';
import { type BettingInsightsConfigFactoryOptions, type MatchupCentreIndex } from '~/configuration/types';
import { type BettingInsightsUIConfig, createBettingInsightsUIConfig } from '~/configuration/ui/BettingInsightsUI';
import { FILTERS_CONFIGS } from '~/search-domain/config/filters-config';
import { EVENT_DISPLAY_NAME, getEventDisplayNames } from '~/search-domain/constants/eventDisplayNames';
import { matchupCentreIndex } from '~/search-domain/indexes';

export function createMatchupCentreUIConfig(
  requestContext: RequestContext,
  _options: BettingInsightsConfigFactoryOptions = {},
  tags: string[],
) {
  const filterTranslations = FILTERS_CONFIGS[requestContext.repubConfig.name as keyof typeof FILTERS_CONFIGS];

  const factoryRepub = requestContext.repubConfig.name;
  const {
    repubCountry: factoryRepubCountry,
    repubIs: factoryRepubIs,
    repubPath: factoryRepubPath,
  } = getRepubMetaFromRepub(factoryRepub);

  const uiConfig: BettingInsightsUIConfig<MatchupCentreIndex, any> = {
    card: {
      styles: {},
      wrapper: (_context, { children }) => children,
      table: {
        body: {
          render(_context, { bookmakers, content, resourcesUrl, table }) {
            return (
              <MatchupCard
                bookmakers={bookmakers}
                content={content}
                isLocked={table.isWalled}
                repub={factoryRepub}
                repubCountry={factoryRepubCountry}
                repubIs={factoryRepubIs}
                repubPath={factoryRepubPath}
                resourcesUrl={resourcesUrl || ''}
              />
            );
          },
        },
        getContent(_context, { result }) {
          return [result];
        },
        headings: {
          content: [],
        },
        cells: [],
      },
      title: {
        format: 'Matchup Centre',
      },
    },
    initialUiState(context) {
      const defaultEventDisplayName =
        REPUB_CONFIG[context.repubConfig.name]?.plus?.matchupCentre.defaultEventDisplayName;

      // No `range` on hit rate here, unlike trends/positive-ev: Match Centre mounts no range widget
      // and does not register the attribute as a facet, so the entry never became a search parameter.
      // Wiring it up would filter on a field this pipeline never retrieves. See SPOR-2400.
      return {
        hitsPerPage: 10,
        // No configured default means "All", which is the slow path — cost scales with subevents in
        // scope. Do not add a hardcoded fallback league here: it belongs per-repub in REPUB_CONFIG,
        // where it can be checked against what actually has fixtures.
        ...(defaultEventDisplayName ? { refinementList: { eventDisplayName: [defaultEventDisplayName] } } : {}),
      };
    },
    refinements: {
      clearAll: {
        excludedAttributes: [],
      },
      components: [
        {
          attribute: 'subeventStartDate',
          getLabel(_context) {
            return filterTranslations.date;
          },
          render(context) {
            return (
              <DateRefinement
                attribute={context.attribute}
                isDisabled={context.isWalled}
                label={filterTranslations.date}
                repubCountry={factoryRepubCountry}
              />
            );
          },
        },
      ],
    },
    results: {
      categories(context) {
        const repubName = context.repubConfig.name;
        const categoryOrdering =
          CATEGORY_ORDERING_CONFIGS[repubName as keyof typeof CATEGORY_ORDERING_CONFIGS] ??
          CATEGORY_ORDERING_CONFIGS.OC;

        return {
          attribute: EVENT_DISPLAY_NAME,
          labels: getEventDisplayNames(repubName),
          order: categoryOrdering,
        };
      },
      labelledBy: '#bet-name',
      propsName: 'bets',
    },
    search: {
      render(context, { searchAttributes }) {
        return (
          <Autocomplete
            disabled={context.isWalled}
            indexName={matchupCentreIndex}
            placeholder={filterTranslations.matchCenterSearchPlaceholder}
            repub={context.repubConfig.name}
            searchAttributes={searchAttributes}
          />
        );
      },
    },
    sorting: {
      canSort: () => false,
    },
  };

  return { ui: createBettingInsightsUIConfig(requestContext, uiConfig, tags) };
}
